import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { AppDispatch, AppThunk } from "@/app/types";
import {
  fetchScheduledReports,
  createScheduledReport,
  updateScheduledReport,
  deleteScheduledReport,
  ScheduledReportItem,
  ScheduledReportFormData,
} from "../api/scheduledReportsApi";

interface ScheduledReportsState {
  reports: ScheduledReportItem[];
  totalCount: number;
  fetchLoading: boolean;
  fetchError: string | null;
  saveLoading: boolean;
  saveError: string | null;
  deleteLoading: boolean;
  deleteError: string | null;
}

const initialState: ScheduledReportsState = {
  reports: [],
  totalCount: 0,
  fetchLoading: false,
  fetchError: null,
  saveLoading: false,
  saveError: null,
  deleteLoading: false,
  deleteError: null,
};

export const scheduledReportsSlice = createSlice({
  name: "scheduledReports",
  initialState,
  reducers: {
    setFetchLoading(state, action: PayloadAction<boolean>) {
      state.fetchLoading = action.payload;
    },
    setFetchError(state, action: PayloadAction<string | null>) {
      state.fetchError = action.payload;
    },
    setReports(state, action: PayloadAction<ScheduledReportItem[]>) {
      state.reports = action.payload;
    },
    setTotalCount(state, action: PayloadAction<number>) {
      state.totalCount = action.payload;
    },
    setSaveLoading(state, action: PayloadAction<boolean>) {
      state.saveLoading = action.payload;
    },
    setSaveError(state, action: PayloadAction<string | null>) {
      state.saveError = action.payload;
    },
    setDeleteLoading(state, action: PayloadAction<boolean>) {
      state.deleteLoading = action.payload;
    },
    setDeleteError(state, action: PayloadAction<string | null>) {
      state.deleteError = action.payload;
    },
  },
});

export const {
  setFetchLoading,
  setFetchError,
  setReports,
  setTotalCount,
  setSaveLoading,
  setSaveError,
  setDeleteLoading,
  setDeleteError,
} = scheduledReportsSlice.actions;

export default scheduledReportsSlice.reducer;

export const fetchScheduledReportsAction =
  (): AppThunk => async (dispatch: AppDispatch) => {
    dispatch(setFetchLoading(true));
    dispatch(setFetchError(null));
    try {
      const result = await fetchScheduledReports();
      dispatch(setReports(result.data));
      dispatch(setTotalCount(result.meta?.totalCount ?? 0));
    } catch (error) {
      const message =
        error instanceof Error
          ? error.message
          : "Failed to fetch scheduled reports.";
      dispatch(setFetchError(message));
    } finally {
      dispatch(setFetchLoading(false));
    }
  };

export const createScheduledReportAction =
  (data: ScheduledReportFormData, onSuccess?: () => void): AppThunk =>
  async (dispatch: AppDispatch) => {
    dispatch(setSaveLoading(true));
    dispatch(setSaveError(null));
    try {
      await createScheduledReport(data);
      onSuccess?.();
    } catch (error) {
      const message =
        error instanceof Error
          ? error.message
          : "Failed to create scheduled report.";
      dispatch(setSaveError(message));
    } finally {
      dispatch(setSaveLoading(false));
    }
  };

export const updateScheduledReportAction =
  (
    uuid: string,
    data: ScheduledReportFormData,
    onSuccess?: () => void,
  ): AppThunk =>
  async (dispatch: AppDispatch) => {
    dispatch(setSaveLoading(true));
    dispatch(setSaveError(null));
    try {
      await updateScheduledReport(uuid, data);
      onSuccess?.();
    } catch (error) {
      const message =
        error instanceof Error
          ? error.message
          : "Failed to update scheduled report.";
      dispatch(setSaveError(message));
    } finally {
      dispatch(setSaveLoading(false));
    }
  };

export const deleteScheduledReportAction =
  (uuid: string, onSuccess?: () => void): AppThunk =>
  async (dispatch: AppDispatch) => {
    dispatch(setDeleteLoading(true));
    dispatch(setDeleteError(null));
    try {
      await deleteScheduledReport(uuid);
      onSuccess?.();
    } catch (error) {
      const message =
        error instanceof Error
          ? error.message
          : "Failed to delete scheduled report.";
      dispatch(setDeleteError(message));
    } finally {
      dispatch(setDeleteLoading(false));
    }
  };
